import { z } from 'zod';

export const ZODIAC_SIGN_NAMES = [
  'Aries',
  'Taurus',
  'Gemini',
  'Cancer',
  'Leo',
  'Virgo',
  'Libra',
  'Scorpio',
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
] as const;

// Export as ZodiacSignValues for backward compatibility
// Removed legacy alias `ZodiacSignValues` to avoid duplicate-exports noise.

export const zodiacSignEnum = z.enum(ZODIAC_SIGN_NAMES);

export type ZodiacSign = z.infer<typeof zodiacSignEnum>;

// Raw data now lives in the 'lib' package
export const ZODIAC_SIGNS_DATA = [
  {
    name: 'Aries',
    dateRange: 'Mar 21 - Apr 19',
    element: 'Fire',
    path: 'M4 12 L8 8 L12 12 L16 8 L20 12',
  },
  {
    name: 'Taurus',
    dateRange: 'Apr 20 - May 20',
    element: 'Earth',
    path: 'M4 12 Q8 4 12 12 Q16 20 20 12',
  },
  {
    name: 'Gemini',
    dateRange: 'May 21 - Jun 20',
    element: 'Air',
    path: 'M4 8 L20 8 M4 16 L20 16 M12 8 L12 16',
  },
  {
    name: 'Cancer',
    dateRange: 'Jun 21 - Jul 22',
    element: 'Water',
    path: 'M4 12 A8 8 0 0 1 20 12',
  },
  {
    name: 'Leo',
    dateRange: 'Jul 23 - Aug 22',
    element: 'Fire',
    path: 'M4 12 L8 8 L12 12 L16 16 L20 12',
  },
  {
    name: 'Virgo',
    dateRange: 'Aug 23 - Sep 22',
    element: 'Earth',
    path: 'M4 16 L8 8 L12 16 L16 8 L20 16',
  },
  {
    name: 'Libra',
    dateRange: 'Sep 23 - Oct 22',
    element: 'Air',
    path: 'M4 16 L20 16 M8 12 L16 12',
  },
  {
    name: 'Scorpio',
    dateRange: 'Oct 23 - Nov 21',
    element: 'Water',
    path: 'M4 12 L8 16 L12 12 L16 16 L20 12 L16 8',
  },
  {
    name: 'Sagittarius',
    dateRange: 'Nov 22 - Dec 21',
    element: 'Fire',
    path: 'M4 20 L20 4 M12 4 L20 4 M20 12 L20 4',
  },
  {
    name: 'Capricorn',
    dateRange: 'Dec 22 - Jan 19',
    element: 'Earth',
    path: 'M4 16 L8 12 L12 16 L16 12 L20 16 L20 8',
  },
  {
    name: 'Aquarius',
    dateRange: 'Jan 20 - Feb 18',
    element: 'Air',
    path: 'M4 10 L8 14 L12 10 L16 14 L20 10',
  },
  {
    name: 'Pisces',
    dateRange: 'Feb 19 - Mar 20',
    element: 'Water',
    path: 'M4 8 A8 8 0 0 0 12 16 A8 8 0 0 0 20 8',
  },
] as const;

// Date validation constants
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY = 1;
const MAX_DAY = 31;

// Zodiac date ranges (month, startDay, endMonth, endDay)
const ZODIAC_RANGES = [
  {
    sign: 'Aries' as ZodiacSign,
    startMonth: 3,
    startDay: 21,
    endMonth: 4,
    endDay: 19,
  },
  {
    sign: 'Taurus' as ZodiacSign,
    startMonth: 4,
    startDay: 20,
    endMonth: 5,
    endDay: 20,
  },
  {
    sign: 'Gemini' as ZodiacSign,
    startMonth: 5,
    startDay: 21,
    endMonth: 6,
    endDay: 20,
  },
  {
    sign: 'Cancer' as ZodiacSign,
    startMonth: 6,
    startDay: 21,
    endMonth: 7,
    endDay: 22,
  },
  {
    sign: 'Leo' as ZodiacSign,
    startMonth: 7,
    startDay: 23,
    endMonth: 8,
    endDay: 22,
  },
  {
    sign: 'Virgo' as ZodiacSign,
    startMonth: 8,
    startDay: 23,
    endMonth: 9,
    endDay: 22,
  },
  {
    sign: 'Libra' as ZodiacSign,
    startMonth: 9,
    startDay: 23,
    endMonth: 10,
    endDay: 22,
  },
  {
    sign: 'Scorpio' as ZodiacSign,
    startMonth: 10,
    startDay: 23,
    endMonth: 11,
    endDay: 21,
  },
  {
    sign: 'Sagittarius' as ZodiacSign,
    startMonth: 11,
    startDay: 22,
    endMonth: 12,
    endDay: 21,
  },
  {
    sign: 'Capricorn' as ZodiacSign,
    startMonth: 12,
    startDay: 22,
    endMonth: 1,
    endDay: 19,
  },
  {
    sign: 'Aquarius' as ZodiacSign,
    startMonth: 1,
    startDay: 20,
    endMonth: 2,
    endDay: 18,
  },
  {
    sign: 'Pisces' as ZodiacSign,
    startMonth: 2,
    startDay: 19,
    endMonth: 3,
    endDay: 20,
  },
] as const;

// Export ZODIAC_SIGNS as an alias for ZODIAC_SIGNS_DATA for backward compatibility
// Removed legacy alias `ZODIAC_SIGNS` to avoid duplicate-exports noise.

const isValidDate = (_year: number, month: number, day: number): boolean => {
  return (
    !(Number.isNaN(month) || Number.isNaN(day)) &&
    month >= MIN_MONTH &&
    month <= MAX_MONTH &&
    day >= MIN_DAY &&
    day <= MAX_DAY
  );
};

type DateRange = {
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
};

const isDateInRange = (month: number, day: number, range: DateRange): boolean => {
  const { startMonth, startDay, endMonth, endDay } = range;

  // Handle year boundary (e.g., Capricorn spans December to January)
  if (startMonth > endMonth) {
    return (month === startMonth && day >= startDay) || (month === endMonth && day <= endDay);
  }
  return (month === startMonth && day >= startDay) || (month === endMonth && day <= endDay);
};

const findZodiacSign = (month: number, day: number): ZodiacSign | null => {
  for (const range of ZODIAC_RANGES) {
    if (isDateInRange(month, day, range)) {
      return range.sign;
    }
  }
  return null;
};

export const getZodiacSignFromDate = (dateString: string): ZodiacSign | null => {
  try {
    console.log(`🎯 [ZODIAC] Getting sign for date: ${dateString}`);

    // Handle ISO string format with time component
    let cleanDateString = dateString;
    if (dateString.includes('T')) {
      cleanDateString = dateString.split('T')[0];
    }

    // Handles YYYY-MM-DD format from <input type="date">
    const [year, month, day] = cleanDateString.split('-').map(Number);

    console.log(`🎯 [ZODIAC] Parsed: year=${year}, month=${month}, day=${day}`);

    if (!isValidDate(year, month, day)) {
      console.error(`🎯 [ZODIAC] Invalid date: year=${year}, month=${month}, day=${day}`);
      return null;
    }

    // For zodiac sign determination, we use the calendar date directly
    // NOT JS date manipulation which can cause timezone issues
    const m = month;
    const d = day;

    console.log(`🎯 [ZODIAC] Final: month=${m}, day=${d}`);

    const sign = findZodiacSign(m, d);
    console.log(`🎯 [ZODIAC] Found sign: ${sign}`);
    console.log(`🎯 [ZODIAC] DEBUG: Checking Leo range (Jul 23 - Aug 22): month=${m}, day=${d}`);
    console.log(`🎯 [ZODIAC] DEBUG: Is after Jul 23? (${m} > 7 or (${m} === 7 and ${d} >= 23))`);
    console.log(`🎯 [ZODIAC] DEBUG: Is before Aug 22? (${m} < 8 or (${m} === 8 and ${d} <= 22))`);

    return sign;
  } catch (error) {
    console.error(`🎯 [ZODIAC] Error parsing date "${dateString}":`, error);
    return null;
  }
};
